import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";
import api from "./services/api";
import "./App.css";

const emptyAddress = {
  fullName: "",
  street: "",
  city: "",
  state: "",
  pincode: "",
  mobile: ""
};

const defaultPackageSizesKg = [1, 5, 10, 50];
const deliveryStatuses = [
  "ORDER_PLACED",
  "PACKED",
  "SHIPPED",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "CANCELLED"
];

function App() {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    return {
      token,
      user: user ? JSON.parse(user) : null
    };
  });
  const [cartCount, setCartCount] = useState(0);

  const isAuthenticated = Boolean(auth.token);
  const isAdmin = auth.user?.role === "ROLE_ADMIN";

  const saveAuth = (payload) => {
    localStorage.setItem("token", payload.token);
    localStorage.setItem("user", JSON.stringify(payload.user));
    setAuth(payload);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setAuth({ token: null, user: null });
    setCartCount(0);
  };

  const refreshCartCount = async () => {
    if (!localStorage.getItem("token")) {
      setCartCount(0);
      return;
    }

    try {
      const { data } = await api.get("/api/cart");
      setCartCount(data.reduce((sum, item) => sum + item.quantity, 0));
    } catch {
      setCartCount(0);
    }
  };

  useEffect(() => {
    refreshCartCount();
  }, [auth.token]);

  return (
    <BrowserRouter>
      <div className="app-shell">
        <Navbar
          user={auth.user}
          cartCount={cartCount}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          onLogout={logout}
        />

        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route
              path="/login"
              element={<Login onAuthenticated={saveAuth} onCartChanged={refreshCartCount} />}
            />
            <Route
              path="/register"
              element={<Register onAuthenticated={saveAuth} onCartChanged={refreshCartCount} />}
            />
            <Route
              path="/products"
              element={<Products isAuthenticated={isAuthenticated} isAdmin={isAdmin} onCartChanged={refreshCartCount} />}
            />
            <Route
              path="/cart"
              element={<Cart isAuthenticated={isAuthenticated} onCartChanged={refreshCartCount} />}
            />
            <Route path="/orders" element={<Orders isAuthenticated={isAuthenticated} isAdmin={isAdmin} />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </main>

        <footer className="site-footer">
          <strong>Suvarnraj Organic Farms</strong>
          <span>Pure organic products direct from farm</span>
        </footer>
      </div>
    </BrowserRouter>
  );
}

function Navbar({ user, cartCount, isAuthenticated, isAdmin, onLogout }) {
  return (
    <header className="site-header">
      <Link className="brand" to="/">
        <img
          className="brand-logo"
          src="/videos/suvarnraj logo.jpeg"
          alt="Suvarnraj Organic Farms"
        />
        <span>Suvarnraj Organic Farms</span>
      </Link>

      <nav className="nav-links">
        <NavLink to="/">Home</NavLink>
        <NavLink to="/products">Products</NavLink>
        <NavLink to="/cart">Cart ({cartCount})</NavLink>
        <NavLink to="/orders">Orders</NavLink>
        <NavLink to="/about">About</NavLink>
      </nav>

      <div className="nav-actions">
        {isAuthenticated ? (
          <>
            <span className="user-chip">{user?.username}{isAdmin ? " · Admin" : ""}</span>
            <button className="ghost-button" onClick={onLogout}>Logout</button>
          </>
        ) : (
          <>
            <Link className="text-button" to="/login">Login</Link>
            <Link className="primary-button" to="/register">Register</Link>
          </>
        )}
      </div>
    </header>
  );
}

function Home() {
  return (
    <>
      <section className="hero">
        <div className="hero-copy">
          <img
            className="hero-logo"
            src="/videos/suvarnraj logo.jpeg"
            alt="Suvarnraj Organic Farms"
          />
          <span className="eyebrow">Pure Organic, Pure Trust</span>
          <h1>Suvarnraj Organic Farms</h1>
          <p>
            Farm to Home - 100% Natural & Chemical-Free Grains
          </p>
          <div className="hero-highlights">
            <span>Direct from Farmers</span>
            <span>100% Natural & Organic</span>
            <span>Hygienic Packaging</span>
            <span>Trusted Quality</span>
          </div>
          <div className="hero-actions">
            <Link className="primary-button" to="/products">Shop products</Link>
            <Link className="secondary-button" to="/about">Our story</Link>
          </div>
        </div>
        <div className="hero-panel">
          <strong>Today&apos;s farm basket</strong>
          <span>Wheat, rice, dal, and pantry essentials</span>
        </div>
      </section>

      <section className="trust-strip">
        <div>
          <strong>Farm focused</strong>
          <span>Clean product catalog</span>
        </div>
        <div>
          <strong>Secure login</strong>
          <span>JWT protected cart and orders</span>
        </div>
        <div>
          <strong>Payment status</strong>
          <span>COD, UPI, and card tracking</span>
        </div>
      </section>
    </>
  );
}

function ProductVisual({ product }) {
  const name = product.name.toLowerCase();
  const src = getProductImage(name);

  return <img className="product-image" src={src} alt={product.name} />;
}

function getProductImage(name) {
  if (name.includes("basmati")) return "/products/basmati-rice.png";
  if (name.includes("kolam")) return "/products/kolam-rice.png";
  if (name.includes("indrayani")) return "/products/indrayani-rice.png";
  if (name.includes("toor")) return "/products/toor-dal.png";
  if (name.includes("moong")) return "/products/moong-dal.png";
  if (name.includes("chana")) return "/products/chana-dal.png";
  if (name.includes("wheat")) return "/products/organic-wheat.png";
  if (name.includes("rice")) return "/products/basmati-rice.png";
  return "/products/organic-wheat.png";
}

function ProductCard({ product, adding, onAdd, isAdmin, onEdit, onDelete }) {
  const packageSizes = product.packageSizesKg?.length ? product.packageSizesKg : defaultPackageSizesKg;
  const [packageSizeKg, setPackageSizeKg] = useState(String(packageSizes[0]));

  return (
    <article className="product-card">
      <ProductVisual product={product} />
      <div className="product-body">
        <div>
          <span className="product-tag">Organic pantry</span>
          <h2>{product.name}</h2>
          <p>Fresh stock available for home delivery checkout.</p>
        </div>
        <div className="product-footer">
          <strong>{formatCurrency(product.price)} / kg</strong>
          <label className="compact-field">
            Bag
            <select value={packageSizeKg} onChange={(event) => setPackageSizeKg(event.target.value)}>
              {packageSizes.map((size) => (
                <option key={size} value={size}>{formatPackageSize(size)}</option>
              ))}
            </select>
          </label>
          <button
            className="primary-button"
            onClick={() => onAdd(product.id, Number(packageSizeKg))}
            disabled={adding}
          >
            {adding ? "Adding..." : "Add to cart"}
          </button>
        </div>
        {isAdmin && (
          <div className="admin-actions">
            <button className="ghost-button" type="button" onClick={() => onEdit(product)}>Edit</button>
            <button className="danger-button" type="button" onClick={() => onDelete(product.id)}>Delete</button>
          </div>
        )}
      </div>
    </article>
  );
}

function ProductEditor({ product, onCancel, onSave }) {
  const [form, setForm] = useState({
    id: product.id,
    name: product.name,
    price: product.price,
    packageSizesKg: (product.packageSizesKg?.length ? product.packageSizesKg : defaultPackageSizesKg).join(", ")
  });

  const submit = (event) => {
    event.preventDefault();
    onSave(form);
  };

  return (
    <form className="product-editor" onSubmit={submit}>
      <h2>{form.id ? "Edit product" : "Add product"}</h2>
      <div className="field-grid">
        <TextInput
          label="Product name"
          value={form.name}
          onChange={(name) => setForm({ ...form, name })}
        />
        <TextInput
          label="Price"
          type="number"
          value={String(form.price)}
          onChange={(price) => setForm({ ...form, price })}
        />
      </div>
      <TextInput
        label="Package sizes in kg"
        value={form.packageSizesKg}
        onChange={(packageSizesKg) => setForm({ ...form, packageSizesKg })}
        placeholder="1, 5, 10, 50"
      />
      <div className="form-actions">
        <button className="primary-button" type="submit">Save Product</button>
        <button className="ghost-button" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function Login({ onAuthenticated, onCartChanged }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const { data } = await api.post("/auth/login", form);
      onAuthenticated(data);
      await onCartChanged();
      navigate("/products");
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Login failed") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Login">
      <form className="form-panel" onSubmit={submit}>
        <TextInput label="Username" value={form.username} onChange={(username) => setForm({ ...form, username })} />
        <TextInput label="Password" type="password" value={form.password} onChange={(password) => setForm({ ...form, password })} />
        <StatusMessage status={status} />
        <button className="primary-button full-width" disabled={submitting}>
          {submitting ? "Signing in..." : "Login"}
        </button>
        <p className="form-note">New customer? <Link to="/register">Create an account</Link></p>
      </form>
    </AuthLayout>
  );
}

function Register({ onAuthenticated, onCartChanged }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: "",
    password: "",
    email: "",
    mobile: ""
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const { data } = await api.post("/auth/register", form);
      onAuthenticated(data);
      await onCartChanged();
      navigate("/products");
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Registration failed") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Register">
      <form className="form-panel" onSubmit={submit}>
        <TextInput label="Username" value={form.username} onChange={(username) => setForm({ ...form, username })} />
        <TextInput label="Email" type="email" value={form.email} onChange={(email) => setForm({ ...form, email })} />
        <TextInput label="Mobile" value={form.mobile} onChange={(mobile) => setForm({ ...form, mobile })} />
        <TextInput label="Password" type="password" value={form.password} onChange={(password) => setForm({ ...form, password })} />
        <StatusMessage status={status} />
        <button className="primary-button full-width" disabled={submitting}>
          {submitting ? "Creating account..." : "Register"}
        </button>
        <p className="form-note">Already registered? <Link to="/login">Login</Link></p>
      </form>
    </AuthLayout>
  );
}

function Products({ isAuthenticated, isAdmin, onCartChanged }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(true);
  const [addingId, setAddingId] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => {
    let active = true;

    api.get("/products")
      .then(({ data }) => {
        if (active) {
          setProducts(data);
          setStatus({ type: "", message: "" });
        }
      })
      .catch((error) => {
        if (active) setStatus({ type: "error", message: getErrorMessage(error, "Failed to load products") });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const addToCart = async (productId, packageSizeKg) => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    setAddingId(productId);
    setStatus({ type: "", message: "" });

    try {
      await api.post("/api/cart", { productId, packageSizeKg, quantity: 1 });
      await onCartChanged();
      setStatus({ type: "success", message: `${formatPackageSize(packageSizeKg)} bag added to cart` });
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Could not add product to cart") });
    } finally {
      setAddingId(null);
    }
  };

  const saveProduct = async (product) => {
    setStatus({ type: "", message: "" });

    try {
      const payload = {
        name: product.name,
        price: Number(product.price),
        packageSizesKg: parsePackageSizes(product.packageSizesKg)
      };

      if (product.id) {
        const { data } = await api.put(`/products/${product.id}`, payload);
        setProducts((current) => current.map((item) => item.id === data.id ? data : item));
        setStatus({ type: "success", message: "Product updated" });
      } else {
        const { data } = await api.post("/products", payload);
        setProducts((current) => [...current, data]);
        setStatus({ type: "success", message: "Product added" });
      }

      setEditingProduct(null);
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Could not save product") });
    }
  };

  const deleteProduct = async (productId) => {
    setStatus({ type: "", message: "" });

    try {
      await api.delete(`/products/${productId}`);
      setProducts((current) => current.filter((item) => item.id !== productId));
      setStatus({ type: "success", message: "Product deleted" });
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Could not delete product") });
    }
  };

  return (
    <section className="page-section">
      <div className="section-heading">
        <div>
          <h1>Products</h1>
          <p>Browse available farm products and add them to your cart.</p>
        </div>
        {isAdmin && (
          <button
            className="primary-button"
            onClick={() => setEditingProduct({ name: "", price: "" })}
          >
            Add Product
          </button>
        )}
      </div>

      <StatusMessage status={status} />
      {isAdmin && editingProduct && (
        <ProductEditor
          product={editingProduct}
          onCancel={() => setEditingProduct(null)}
          onSave={saveProduct}
        />
      )}

      {loading ? (
        <p className="muted">Loading products...</p>
      ) : (
        <div className="product-grid">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              adding={addingId === product.id}
              onAdd={addToCart}
              isAdmin={isAdmin}
              onEdit={setEditingProduct}
              onDelete={deleteProduct}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function Cart({ isAuthenticated, onCartChanged }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(true);
  const [checkout, setCheckout] = useState({
    address: emptyAddress,
    paymentMethod: "COD"
  });
  const [placingOrder, setPlacingOrder] = useState(false);

  const loadCart = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get("/api/cart");
      setItems(data);
      setStatus({ type: "", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Failed to load cart") });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const total = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.lineTotal), 0),
    [items]
  );
  const deliveryCharge = total > 0 ? 0 : 0;
  const grandTotal = total + deliveryCharge;

  const removeItem = async (itemId) => {
    await api.delete(`/api/cart/${itemId}`);
    await loadCart();
    await onCartChanged();
  };

  const updateCartQuantity = async (item, nextQuantity) => {
    if (nextQuantity < 1) {
      await removeItem(item.id);
      return;
    }

    try {
      await api.put(`/api/cart/${item.id}`, {
        productId: item.productId,
        packageSizeKg: item.packageSizeKg,
        quantity: nextQuantity
      });
      await loadCart();
      await onCartChanged();
      setStatus({ type: "success", message: "Cart quantity updated" });
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Could not update cart quantity") });
    }
  };

  const placeOrder = async (event) => {
    event.preventDefault();
    setPlacingOrder(true);
    setStatus({ type: "", message: "" });

    const payload = {
      address: checkout.address,
      paymentMethod: checkout.paymentMethod
    };

    try {
      const { data } = await api.post("/api/orders", payload);

      if (data.razorpayRequired) {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          setStatus({ type: "error", message: "Unable to load Razorpay checkout" });
          return;
        }

        const options = {
          key: data.razorpayKeyId,
          amount: data.amount,
          currency: data.currency,
          name: "Suvarnraj Organic Farms",
          description: `${checkout.paymentMethod} payment`,
          order_id: data.razorpayOrderId,
          prefill: {
            name: checkout.address.fullName,
            contact: checkout.address.mobile
          },
          theme: {
            color: "#2f7d32"
          },
          handler: async (razorpayResponse) => {
            try {
              const verified = await api.post("/api/orders/verify-payment", {
                orderId: data.order.id,
                razorpayOrderId: razorpayResponse.razorpay_order_id,
                razorpayPaymentId: razorpayResponse.razorpay_payment_id,
                razorpaySignature: razorpayResponse.razorpay_signature
              });
              await onCartChanged();
              navigate("/orders", {
                state: {
                  message: `Order #${verified.data.id} paid successfully`
                }
              });
            } catch (error) {
              setStatus({ type: "error", message: getErrorMessage(error, "Payment verification failed") });
            }
          },
          modal: {
            ondismiss: () => {
              setStatus({ type: "error", message: "Payment was not completed. Your order is pending." });
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
        return;
      }

      await onCartChanged();
      navigate("/orders", {
        state: {
          message: `Order #${data.order.id} placed with ${data.order.status} payment status`
        }
      });
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Could not place order") });
    } finally {
      setPlacingOrder(false);
    }
  };

  if (!isAuthenticated) {
    return <AuthRequired title="Your cart" />;
  }

  return (
    <section className="page-section two-column">
      <div>
        <div className="section-heading">
          <div>
            <h1>Cart</h1>
            <p>Review items before checkout.</p>
          </div>
        </div>

        <StatusMessage status={status} />

        {loading ? (
          <p className="muted">Loading cart...</p>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <h2>Your cart is empty</h2>
            <Link className="primary-button" to="/products">Shop products</Link>
          </div>
        ) : (
          <div className="list-panel">
            {items.map((item) => (
              <div className="cart-row" key={item.id}>
                <div>
                  <strong>{item.productName}</strong>
                  <span>{formatPackageSize(item.packageSizeKg)} bag · {formatCurrency(item.unitPrice)} / kg x {item.quantity}</span>
                </div>
                <div className="quantity-control" aria-label={`Quantity for ${item.productName}`}>
                  <button
                    className="step-button"
                    type="button"
                    onClick={() => updateCartQuantity(item, item.quantity - 1)}
                  >
                    -
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    className="step-button"
                    type="button"
                    onClick={() => updateCartQuantity(item, item.quantity + 1)}
                  >
                    +
                  </button>
                </div>
                <div className="row-actions">
                  <strong>{formatCurrency(item.lineTotal)}</strong>
                  <button className="ghost-button" onClick={() => removeItem(item.id)}>Remove</button>
                </div>
              </div>
            ))}
            <div className="cart-total">
              <span>Total</span>
              <strong>{formatCurrency(total)}</strong>
            </div>
          </div>
        )}
      </div>

      <form className="checkout-panel" onSubmit={placeOrder}>
        <div>
          <span className="checkout-step">Step 1 of 2</span>
          <h2>Delivery address</h2>
        </div>
        <AddressFields
          value={checkout.address}
          onChange={(address) => setCheckout({ ...checkout, address })}
        />
        <div>
          <span className="checkout-step">Step 2 of 2</span>
          <h2>Payment</h2>
        </div>
        <label>
          Payment method
          <select
            value={checkout.paymentMethod}
            onChange={(event) => setCheckout({ ...checkout, paymentMethod: event.target.value })}
          >
            <option value="COD">Cash on delivery</option>
            <option value="UPI">UPI</option>
            <option value="CARD">Card</option>
          </select>
        </label>
        {checkout.paymentMethod !== "COD" && (
          <p className="muted">Secure online payment opens in Razorpay after placing the order.</p>
        )}
        <div className="checkout-summary">
          <div>
            <span>Subtotal</span>
            <strong>{formatCurrency(total)}</strong>
          </div>
          <div>
            <span>Delivery</span>
            <strong>{deliveryCharge === 0 ? "Free" : formatCurrency(deliveryCharge)}</strong>
          </div>
          <div>
            <span>Grand total</span>
            <strong>{formatCurrency(grandTotal)}</strong>
          </div>
        </div>
        <button className="primary-button full-width" disabled={items.length === 0 || placingOrder}>
          {placingOrder ? "Placing order..." : "Place order"}
        </button>
      </form>
    </section>
  );
}

function Orders({ isAuthenticated, isAdmin }) {
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(true);

  const loadOrders = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get(isAdmin ? "/api/orders/admin/all" : "/api/orders");
      setOrders(data);
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Failed to load orders") });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isAdmin]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const updateDeliveryTracking = async (orderId, tracking) => {
    setStatus({ type: "", message: "" });

    try {
      const { data } = await api.put(`/api/orders/${orderId}/delivery`, tracking);
      setOrders((current) => current.map((order) => order.id === data.id ? data : order));
      setStatus({ type: "success", message: `Order #${data.id} tracking updated` });
    } catch (error) {
      setStatus({ type: "error", message: getErrorMessage(error, "Could not update tracking") });
    }
  };

  if (!isAuthenticated) {
    return <AuthRequired title="Orders" />;
  }

  return (
    <section className="page-section">
      <div className="section-heading">
        <div>
          <h1>Orders</h1>
          <p>{isAdmin ? "Manage customer delivery tracking and payment status." : "Track payment status, delivery status, and order totals."}</p>
        </div>
      </div>

      <StatusMessage status={status} />
      <StatusMessage status={{ type: "success", message: location.state?.message || "" }} />

      {loading ? (
        <p className="muted">Loading orders...</p>
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <h2>No orders yet</h2>
          <Link className="primary-button" to="/products">Shop products</Link>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => (
            <article className="order-card" key={order.id}>
              <div className="order-header">
                <div>
                  <h2>Order #{order.id}</h2>
                  <span>{formatDate(order.orderDate)}</span>
                  {isAdmin && (
                    <span>{order.customerName} · {order.customerMobile || "No mobile"}</span>
                  )}
                </div>
                <span className={`status-badge ${order.status.toLowerCase()}`}>{order.status}</span>
              </div>
              <div className="order-meta">
                <span>{order.paymentMethod}</span>
                <span>{order.paymentReference || "No reference"}</span>
                <strong>{formatCurrency(order.totalAmount)}</strong>
              </div>
              <p className="muted">{order.paymentMessage}</p>
              <div className="tracking-panel">
                <div>
                  <span>Delivery</span>
                  <strong>{formatDeliveryStatus(order.deliveryStatus)}</strong>
                </div>
                <div>
                  <span>Courier</span>
                  <strong>{order.courierName || "Not assigned"}</strong>
                </div>
                <div>
                  <span>Tracking No.</span>
                  <strong>{order.trackingNumber || "Not assigned"}</strong>
                </div>
              </div>
              {isAdmin && (
                <DeliveryTrackingEditor order={order} onSave={updateDeliveryTracking} />
              )}
              <div className="order-items">
                {order.items.map((item) => (
                  <span key={`${order.id}-${item.productId}`}>
                    {item.productName} x {item.quantity}
                    {item.packageSizeKg ? ` (${formatPackageSize(item.packageSizeKg)})` : ""}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function DeliveryTrackingEditor({ order, onSave }) {
  const [tracking, setTracking] = useState({
    deliveryStatus: order.deliveryStatus || "ORDER_PLACED",
    courierName: order.courierName || "",
    trackingNumber: order.trackingNumber || ""
  });

  useEffect(() => {
    setTracking({
      deliveryStatus: order.deliveryStatus || "ORDER_PLACED",
      courierName: order.courierName || "",
      trackingNumber: order.trackingNumber || ""
    });
  }, [order]);

  const submit = (event) => {
    event.preventDefault();
    onSave(order.id, tracking);
  };

  return (
    <form className="tracking-editor" onSubmit={submit}>
      <label>
        Delivery status
        <select
          value={tracking.deliveryStatus}
          onChange={(event) => setTracking({ ...tracking, deliveryStatus: event.target.value })}
        >
          {deliveryStatuses.map((deliveryStatus) => (
            <option key={deliveryStatus} value={deliveryStatus}>
              {formatDeliveryStatus(deliveryStatus)}
            </option>
          ))}
        </select>
      </label>
      <TextInput
        label="Courier"
        value={tracking.courierName}
        onChange={(courierName) => setTracking({ ...tracking, courierName })}
        placeholder="Delhivery / DTDC"
        required={false}
      />
      <TextInput
        label="Tracking number"
        value={tracking.trackingNumber}
        onChange={(trackingNumber) => setTracking({ ...tracking, trackingNumber })}
        placeholder="TRK123456"
        required={false}
      />
      <button className="ghost-button" type="submit">Update Tracking</button>
    </form>
  );
}

function About() {
  return (
    <section className="page-section narrow about-content">
      <span className="eyebrow">About Us</span>
      <h1>Suvarnraj Organic Farms</h1>
      <p>
        Suvarnraj Organic Farms is committed to providing pure, natural, and chemical-free
        food grains directly from farms to your home.
      </p>
      <p>
        We work closely with farmers who follow traditional and sustainable farming practices
        to ensure freshness, quality, and authenticity in every product.
      </p>
      <p>
        At Suvarnraj Organic Farms, we believe that food should be clean, honest, and close
        to nature. Our goal is to make organic living simple, affordable, and accessible for
        every household.
      </p>

      <div className="about-highlight">
        <h2>Vision</h2>
        <p>
          To build a trusted brand that represents purity, quality, and authenticity in
          organic food while supporting farmers and promoting healthy living.
        </p>
      </div>

      <div className="mission-list">
        <h2>Mission</h2>
        <ul>
          <li>Provide high-quality organic products</li>
          <li>Maintain trust and transparency</li>
          <li>Support farmers</li>
          <li>Deliver chemical-free food</li>
        </ul>
      </div>

      <div className="contact-details">
        <h2>Contact Details</h2>
        <p>Email: <a href="mailto:suvarnrajorganicfarms@gmail.com">suvarnrajorganicfarms@gmail.com</a></p>
        <p>Website: <a href="https://www.suvarnrajorganicfarms.com" target="_blank" rel="noreferrer">www.suvarnrajorganicfarms.com</a></p>
      </div>
    </section>
  );
}

function AuthLayout({ title, children }) {
  return (
    <section className="auth-page">
      <div className="auth-card">
        <h1>{title}</h1>
        {children}
      </div>
    </section>
  );
}

function AuthRequired({ title }) {
  return (
    <section className="page-section narrow">
      <div className="empty-state">
        <h1>{title}</h1>
        <p>Please login to continue.</p>
        <Link className="primary-button" to="/login">Login</Link>
      </div>
    </section>
  );
}

function AddressFields({ value, onChange }) {
  const update = (field, fieldValue) => onChange({ ...value, [field]: fieldValue });

  return (
    <>
      <TextInput label="Full name" value={value.fullName} onChange={(next) => update("fullName", next)} />
      <TextInput label="Street" value={value.street} onChange={(next) => update("street", next)} />
      <div className="field-grid">
        <TextInput label="City" value={value.city} onChange={(next) => update("city", next)} />
        <TextInput label="State" value={value.state} onChange={(next) => update("state", next)} />
      </div>
      <div className="field-grid">
        <TextInput label="Pincode" value={value.pincode} onChange={(next) => update("pincode", next)} />
        <TextInput label="Mobile" value={value.mobile} onChange={(next) => update("mobile", next)} />
      </div>
    </>
  );
}

function TextInput({ label, value, onChange, type = "text", placeholder = "", required = true }) {
  return (
    <label>
      {label}
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      />
    </label>
  );
}

function StatusMessage({ status }) {
  if (!status.message) return null;
  return <div className={`status-message ${status.type}`}>{status.message}</div>;
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data || fallback;
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function parsePackageSizes(value) {
  if (Array.isArray(value)) {
    return value.map(Number).filter((size) => size > 0);
  }

  const sizes = String(value || "")
    .split(",")
    .map((size) => Number(size.trim()))
    .filter((size) => size > 0);

  return sizes.length ? sizes : defaultPackageSizesKg;
}

function formatPackageSize(value) {
  return `${Number(value || 1).toLocaleString("en-IN")}kg`;
}

function formatDeliveryStatus(value) {
  return String(value || "ORDER_PLACED")
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR"
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

export default App;
